import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { ClearancePass } from "./ClearancePass";

const LINES = [
  { text: "Get tested.", cls: "" },
  { text: "Stay clear.", cls: "outline-text" },
  { text: "Get paid back.", cls: "text-duo" },
];

const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, i, cls }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onSchedule }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-32 pb-16 lg:pt-36">
      <div className="spotlight absolute inset-0 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#FF2A8D]/20 blur-[140px]" />
      <div className="absolute top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#125AA0]/30 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}
            data-testid="hero-badge-14day"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#5B95D6] animate-pulse" /> The 14-day clearance club
          </motion.div>

          <h1 data-testid="hero-heading" className="font-display font-bold tracking-tight leading-[0.95] text-[12vw] sm:text-7xl lg:text-[4.6rem] xl:text-[5.2rem] lg:whitespace-nowrap">
            {LINES.map((l, i) => <MaskLine key={l.text} i={i} cls={l.cls}>{l.text}</MaskLine>)}
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            Sweet Results keeps sex workers, performers, content creators and everyday people on a
            14-day testing rhythm — with an AI nurse on call, honest sex education, $10 off every booking and $10 back every time you test.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }}
            className="mt-10 flex flex-col sm:flex-row gap-3">
            <button data-testid="hero-schedule-cta" onClick={onSchedule}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF2A8D] hover:bg-[#FF54A5] px-7 py-4 font-semibold glow-pink transition-colors">
              Schedule my test <ArrowUpRight className="transition-transform duration-300 group-hover:rotate-45" size={18} />
            </button>
            <a data-testid="hero-waitlist-cta" href="#waitlist"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#5B95D6]/50 hover:bg-[#125AA0]/30 px-7 py-4 font-semibold text-[#5B95D6] transition-colors">
              Join the app waitlist
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div initial={{ opacity: 0, scale: 0.92, clipPath: "inset(18% 18% 18% 18% round 40px)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 40px)" }}
            transition={{ duration: 1.4, delay: 0.4, ease }}
            className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden rounded-[40px]">
            <motion.img style={{ y: imgY }} src="/img/glow.jpg" alt="Portrait in soft pink and blue light"
              className="absolute inset-0 w-full h-[118%] object-cover -top-[9%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A14] via-transparent to-transparent" />
          </motion.div>

          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }}
            className="absolute -bottom-8 left-0 sm:-left-6 lg:-left-16 w-[78%] max-w-[320px]">
            <ClearancePass />
          </motion.div>

          <div className="absolute -top-6 right-2 sm:-right-4 w-28 h-28 hidden sm:block">
            <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
              <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text className="font-mono" fontSize="9.5" letterSpacing="2.5" fill="#F8FAFC">
                <textPath href="#c">TEST · EVERY · 14 · DAYS · GET · REBATES · </textPath>
              </text>
            </svg>
            <ShieldCheck className="absolute inset-0 m-auto text-[#5B95D6]" size={26} />
          </div>
        </div>
      </div>
    </section>
  );
};
